import { gql } from '@apollo/client/core';
import { getSharedApolloClient } from '@/lib/apollo_setup';

/** Resolves a U.S. postal code to `GeocodeResult` (Empire `geocoding.geocode`). */
const GEOCODE_QUERY = gql`
  query GeocodeQuery($input: GeocodeInput!) {
    geocoding {
      geocode(input: $input) {
        location {
          x
          y
        }
        zipCode
        city
        state
        address
      }
    }
  }
`;

/** Point → nearest default-city row (`geocoding.reverseGeocode`). */
const REVERSE_GEOCODE_QUERY = gql`
  query ReverseGeocodeQuery($location: GeoPointInput!) {
    geocoding {
      reverseGeocode(location: $location) {
        location {
          x
          y
        }
        zipCode
        city
        state
        address
      }
    }
  }
`;

/** Longitude (x) and latitude (y), same convention as `GeoPointInput`. */
export interface Coordinates {
  x: number;
  y: number;
}

/** Matches `GeocodeResult` GraphQL: nested `location` plus mailing fields. */
export interface GeocodeResult {
  location: Coordinates;
  zipCode: string;
  city: string;
  state: string;
  address: string;
}

interface GeocodeQueryResponse {
  geocoding?: {
    geocode?: {
      location: {
        x: string;
        y: string;
      };
      zipCode: string;
      city: string;
      state: string;
      address: string;
    } | null;
  } | null;
}

interface ReverseGeocodeQueryResponse {
  geocoding?: {
    reverseGeocode?: {
      location: {
        x: string;
        y: string;
      };
      zipCode: string;
      city: string;
      state: string;
      address: string;
    } | null;
  } | null;
}

function normalizeGeocodePayload(geocodeData: {
  location?: { x: string | null; y: string | null } | null;
  zipCode?: string | null;
  city?: string | null;
  state?: string | null;
  address?: string | null;
}): GeocodeResult | null {
  const loc = geocodeData?.location;
  if (loc == null || loc.x == null || loc.y == null) {
    console.warn('[geocode] GeocodeResult payload missing location.x or location.y; check API payload.');
    return null;
  }

  const x = Number(loc.x);
  const y = Number(loc.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    console.warn('[geocode] GeocodeResult location coordinates are not finite numbers after coercion; check API payload.');
    return null;
  }

  const zipCode = geocodeData.zipCode;
  const city = geocodeData.city;
  const state = geocodeData.state;
  const address = geocodeData.address;
  if (zipCode == null || city == null || state == null || address == null) {
    console.warn(
      '[geocode] GeocodeResult missing expected mailing fields (zipCode, city, state, or address); check API payload.'
    );
    return null;
  }

  return {
    location: { x, y },
    zipCode,
    city,
    state,
    address,
  };
}

/** Matches GraphQL `ZipCode` scalar (`Scalars::ZipCode`): exactly five digits. */
export function isValidZipcode(zipcode: string | null | undefined): boolean {
  if (!zipcode) return false;

  // Must be exactly 5 digits
  if (!/^\d{5}$/.test(zipcode)) return false;

  // Reject obvious fake/test ZIPs like 00000, 11111, etc.
  if (/^(\d)\1{4}$/.test(zipcode)) return false;

  return true;
}

/** Forward ZIP → point + mailing fields via Empire GraphQL. */
export async function geocode(zipCode: string | null | undefined): Promise<GeocodeResult | null> {
  if (!zipCode || !isValidZipcode(zipCode)) {
    return null;
  }

  let data: GeocodeQueryResponse | undefined;
  let errors: readonly { message: string }[] | undefined;
  try {
    const result = await getSharedApolloClient().query<GeocodeQueryResponse>({
      query: GEOCODE_QUERY,
      variables: { input: { zipCode } },
      errorPolicy: 'all',
    });
    data = result.data;
    errors = result.errors;
  } catch (error) {
    console.error('[geocode] GeocodeQuery request failed:', error);
    return null;
  }

  if (errors?.length) {
    console.error('[geocode] GeocodeQuery GraphQL errors:', errors.map((e) => e.message).join('; '));
    return null;
  }

  const geocodeData = data?.geocoding?.geocode;
  if (!geocodeData) {
    console.warn('[geocode] GeocodeQuery returned no geocode payload; check response or partial errors.');
    return null;
  }

  return normalizeGeocodePayload(geocodeData);
}

/** Reverse geocode: coordinates → nearest location row (Empire `geocoding.reverseGeocode`). */
export async function reverseGeocode(point: Coordinates | null | undefined): Promise<GeocodeResult | null> {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    return null;
  }

  let data: ReverseGeocodeQueryResponse | undefined;
  let errors: readonly { message: string }[] | undefined;
  try {
    const result = await getSharedApolloClient().query<ReverseGeocodeQueryResponse>({
      query: REVERSE_GEOCODE_QUERY,
      variables: { location: { x: point.x, y: point.y } },
      errorPolicy: 'all',
    });
    data = result.data;
    errors = result.errors;
  } catch (error) {
    console.error('[geocode] ReverseGeocodeQuery request failed:', error);
    return null;
  }

  if (errors?.length) {
    console.error('[geocode] ReverseGeocodeQuery GraphQL errors:', errors.map((e) => e.message).join('; '));
    return null;
  }

  const payload = data?.geocoding?.reverseGeocode;
  if (!payload) {
    console.warn('[geocode] ReverseGeocodeQuery returned no reverseGeocode payload; check response or partial errors.');
    return null;
  }

  return normalizeGeocodePayload(payload);
}
